/**
 * Stripe Invoice Service
 *
 * Calls Stripe invoice endpoints via gobodo-api-promise (StripeAccountsApi).
 * Used only when shouldUseGobodo() is true.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';
import { stripeInvoiceToAppInvoice, type MapStripeInvoiceOptions } from './stripeInvoiceMapper';
import type { Invoice } from '../types/index';

export interface SendStripeInvoiceResult {
  invoice?: any;
  failureException?: string;
}

/** Same shape as send - invoice object may contain host_invoice_url after finalize */
export interface FinalizeStripeInvoiceResult {
  invoice?: any;
  failureException?: string;
}

export interface CreateStripeInvoiceFromPricebookInput {
  pricebookIdOrKey: string;
  customerId?: number;
  stripeCustomerId?: string;
  lineItems?: Array<{ priceId: string; quantity: number }>;
  collectionMethod?: string;
  daysUntilDue?: number;
  autoFinalize?: boolean;
  sendAfterFinalize?: boolean;
}

export interface CreateStripeInvoiceFromPricebookResult {
  invoiceId?: string;
  hostInvoiceUrl?: string;
  failureException?: string;
}

/**
 * Finalizes a draft Stripe invoice so it can be paid or shared (payment link becomes available).
 * @param stripeInvoiceId Stripe invoice ID (e.g. in_xxx)
 * @returns Response with invoice object (e.g. host_invoice_url) and optional failure message
 */
export async function finalizeStripeInvoice(stripeInvoiceId: string): Promise<FinalizeStripeInvoiceResult> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.finalizeStripeInvoice(stripeInvoiceId);
  const invoice = data?.invoice ?? data?.getInvoice?.();
  const failureException =
    typeof data?.failureException === 'string'
      ? data.failureException
      : data?.getFailureException?.();
  return { invoice, failureException };
}

/**
 * Sends an open Stripe invoice to the customer by email. (Reserved for future use.)
 * @param stripeInvoiceId Stripe invoice ID (e.g. in_xxx)
 * @returns Response with invoice object (e.g. host_invoice_url) and optional failure message
 */
export async function sendStripeInvoice(stripeInvoiceId: string): Promise<SendStripeInvoiceResult> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.sendStripeInvoice(stripeInvoiceId);
  const invoice = data?.invoice ?? data?.getInvoice?.();
  const failureException =
    typeof data?.failureException === 'string'
      ? data.failureException
      : data?.getFailureException?.();
  return { invoice, failureException };
}

/**
 * Creates a Stripe invoice from a pricebook.
 * Uses customerId (graph id) or stripeCustomerId.
 * Empty lineItems = entire pricebook.

 * @param input Input with pricebookIdOrKey, customerId or stripeCustomerId, optional lineItems, collectionMethod, daysUntilDue, autoFinalize, sendAfterFinalize
 * @returns Normalized result with invoiceId, hostInvoiceUrl, or failureException
 */
export async function createStripeInvoiceFromPricebook(
  input: CreateStripeInvoiceFromPricebookInput
): Promise<CreateStripeInvoiceFromPricebookResult> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client) as any;

  if (typeof api.createStripeInvoiceFromPricebook !== 'function') {
    return {
      failureException:
        'createStripeInvoiceFromPricebook is not available in the Gobodo API. Ensure gobodo-api-promise is updated.',
    };
  }

  const payload: Record<string, unknown> = {
    pricebookIdOrKey: input.pricebookIdOrKey,
    lineItems: input.lineItems ?? [],
  };
  if (input.customerId != null) payload.customerId = input.customerId;
  if (input.stripeCustomerId) payload.stripeCustomerId = input.stripeCustomerId;
  if (input.collectionMethod) payload.collectionMethod = input.collectionMethod;
  if (input.daysUntilDue != null) payload.daysUntilDue = input.daysUntilDue;
  if (input.autoFinalize != null) payload.autoFinalize = input.autoFinalize;
  if (input.sendAfterFinalize != null) payload.sendAfterFinalize = input.sendAfterFinalize;

  const data = await api.createStripeInvoiceFromPricebook(payload);
  const failureException =
    typeof data?.failureException === 'string'
      ? data.failureException
      : data?.getFailureException?.();
  if (failureException) return { failureException };

  const invoice = data?.invoice ?? data?.getInvoice?.();
  const invoiceId =
    invoice?.id ?? invoice?.getId?.() ?? invoice?.invoice_id ?? invoice?.invoiceId;
  const hostInvoiceUrl =
    invoice?.host_invoice_url ??
    invoice?.hostInvoiceUrl ??
    invoice?.invoice_url ??
    invoice?.invoiceUrl;

  return { invoiceId: invoiceId ? String(invoiceId) : undefined, hostInvoiceUrl };
}

export interface ListAllStripeInvoicesOpts {
  limit?: number;
  startingAfter?: string;
}

/**
 * Lists all Stripe invoices (global list).
 * GET /payments/stripe/invoices/all
 */
export async function listAllStripeInvoices(opts?: ListAllStripeInvoicesOpts): Promise<Invoice[]> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client) as any;
  if (typeof api.listAllStripeInvoices !== 'function') {
    return [];
  }
  const data = await api.listAllStripeInvoices(opts ?? {});
  const arr = Array.isArray(data) ? data : [];
  return arr.map((inv: any) => stripeInvoiceToAppInvoice(inv, {}));
}

/**
 * Lists Stripe invoices for a customer (graph customer id = Stripe Customer Account id).
 * GET /payments/stripe/invoices?customerId=
 */
export async function listStripeInvoices(
  customerId: number,
  options?: MapStripeInvoiceOptions
): Promise<Invoice[]> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.listStripeInvoices(customerId);
  const arr = Array.isArray(data) ? data : [];
  return arr.map((inv: any) => stripeInvoiceToAppInvoice(inv, options));
}

/**
 * Gets a single Stripe invoice by id (e.g. in_xxx).
 */
export async function getStripeInvoice(
  invoiceId: string,
  options?: MapStripeInvoiceOptions
): Promise<Invoice | null> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  try {
    const data = await api.getStripeInvoice(invoiceId);
    const inv = data?.invoice ?? data?.getInvoice?.();
    if (!inv) return null;
    return stripeInvoiceToAppInvoice(inv, options);
  } catch {
    return null;
  }
}
